import { Device, MarketLocation, useLocale, useSnackbar, useStartOfWeek } from '@e2m-customer-portal/hooks';
import { InputFieldType, isCogeneration, isCoLocation, isFluctuating } from '@e2m-customer-portal/shared';
import { DateTimePicker, DateTimePickerProps, floorDate, MasterDataItem } from '@e2m-customer-portal/ui';
import { Button, DialogActions, DialogContent } from '@mui/material';
import {
  addHours,
  addMinutes,
  getHours,
  Interval,
  isAfter,
  isBefore,
  isSameDay,
  roundToNearestHours,
  setHours,
  startOfDay,
  startOfHour,
  subSeconds,
  toDate,
} from 'date-fns';
import { ForecastProperty, ForecastTarget, Resolution } from 'generated/graphql';
import { MouseEventHandler, ReactElement, SyntheticEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'react-use';
import {
  getDisplayName,
  getEarliestMarketingDeadline,
  getErrorTranslationKey,
  getMarketingDeadline,
} from '../../utils';
import {
  useRevokeDowntimeMutation,
  useRevokeLegacyDowntimeMutation,
  useSetDowntimeMutation,
  useSetLegacyDowntimeMutation,
} from './queries';

type Reason = 'backdropClick' | 'escapeKeyDown' | 'cancelClick' | 'successClick';

type OnCloseFunc<E extends SyntheticEvent = SyntheticEvent> = (event: E | null, reason: Reason) => void;

const controlEnergyIntervalStep = (date: Date, isEndTime?: boolean) => {
  const intervalStep = isEndTime
    ? ([20, 16, 12, 8, 4, 0].find((hour) => getHours(date) >= hour) ?? 0)
    : ([0, 4, 8, 12, 16, 20].find((hour) => getHours(date) < hour) ?? 24) - 4;
  return startOfHour(setHours(date, intervalStep));
};

const intradayDeadline = (device: Device) =>
  (device.deadlines.find((deadline) => deadline.type === InputFieldType.PRODUCTION)?.deadline
    ?.minutesBeforeIntradayDelivery ?? 15) + 15;

export interface DowntimeModalContentProps {
  /**
   * The date to use as the default value for the start date.
   */
  date?: Date;

  /**
   * The marketLocation to use the devices from.
   */
  marketLocation: MarketLocation;
  device: Device;

  /**
   * Whether the downtime should be set according to a 15 min resolution of schedules.
   */
  currentForecastProperty?: ForecastProperty;

  /**
   * Gets triggered when the dialog should close. The consumer of `DowntimeModal` should then set `open` to false.
   * The given reason indicates the source of the close command. If the source was the submit button, then all
   * required fields were sent to the backend automatically.
   */
  onClose: OnCloseFunc;
}

export const DowntimeModalContent = ({
  date = new Date(),
  marketLocation,
  device,
  currentForecastProperty,
  onClose,
}: Readonly<DowntimeModalContentProps>): ReactElement<DowntimeModalContentProps> => {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const locale = useLocale();
  const { showError, showSuccess } = useSnackbar();
  const isFifteenMinutesResolution = currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES;
  const isVPP = currentForecastProperty?.target === ForecastTarget.VPP;

  const isControlEnergyOnly = isCogeneration(marketLocation.technologies);
  const startOfWeek = useStartOfWeek();
  const minutesStep = isFifteenMinutesResolution ? 15 : undefined;
  // In the case of 15 min resolution, only hours that have not yet started can be selected
  const defaultStart = isFifteenMinutesResolution
    ? addMinutes(floorDate({ minutesStep })(date), intradayDeadline(device))
    : startOfHour(date);
  const [downtimeInterval, setDowntimeInterval] = useState<Interval>({
    start: isControlEnergyOnly ? controlEnergyIntervalStep(date) : defaultStart,
    end: isControlEnergyOnly ? addHours(controlEnergyIntervalStep(date, true), 4) : addHours(defaultStart, 1),
  });

  const isWindOrSolar = isFluctuating(marketLocation.technologies);
  const isColocation = isCoLocation(marketLocation.technologies);

  const { width } = useWindowSize();

  const [reportOrRevoke, setReportOrRevoke] = useState<'report' | 'revoke'>('report');

  const [reportLegacy, { loading: loadingLegacy }] = useSetLegacyDowntimeMutation({
    onError: (error) => {
      const errorTranslationKey = getErrorTranslationKey(error);

      if (
        errorTranslationKey === 'BACKEND_ERROR_DATA_POINTS_ARE_INCOMPLETE' ||
        errorTranslationKey === 'BAD_USER_INPUT'
      ) {
        if (isWindOrSolar) {
          showError(t('DOWNTIME_MODAL_SNACKBAR_ERROR_FLUCTUATING'));
        } else {
          showError(t('DOWNTIME_MODAL_SNACKBAR_ERROR'));
        }
      } else {
        showError(t(errorTranslationKey));
      }
    },
    onCompleted: () => {
      showSuccess(
        t(
          isFifteenMinutesResolution
            ? 'DOWNTIME_MODAL_SNACKBAR_SUCCESS'
            : 'MARKET_LOCATION_AVAILABILITY_OUTAGE_DETAILS_ARIA',
          {
            start: toDate(downtimeInterval.start),
            end: toDate(downtimeInterval.end),
          },
        ),
      );
      onClose(null, 'successClick');
    },
  });

  const [report, { loading }] = useSetDowntimeMutation({
    onError: (error) => showError(t(getErrorTranslationKey(error))),
    onCompleted: () => {
      showSuccess(
        t(
          !isWindOrSolar && !isColocation
            ? 'DOWNTIME_MODAL_SNACKBAR_SUCCESS'
            : 'MARKET_LOCATION_AVAILABILITY_OUTAGE_DETAILS_ARIA',
          {
            start: toDate(downtimeInterval.start),
            end: toDate(downtimeInterval.end),
          },
        ),
      );
      onClose(null, 'successClick');
    },
  });

  const reportDowntime = async (device: Device) => {
    const deadlineDate = getMarketingDeadline(getEarliestMarketingDeadline(device.deadlines));
    const dayAheadAdjustedInterval: Interval<Date> = {
      start: isAfter(downtimeInterval.start, deadlineDate)
        ? roundToNearestHours(toDate(downtimeInterval.start), { roundingMethod: 'floor' })
        : downtimeInterval.start,
      end: isAfter(downtimeInterval.end, deadlineDate)
        ? roundToNearestHours(toDate(downtimeInterval.end), { roundingMethod: 'ceil' })
        : downtimeInterval.end,
    };
    setDowntimeInterval(dayAheadAdjustedInterval);
    isVPP
      ? await report(device.internalId, toDate(dayAheadAdjustedInterval.start), toDate(dayAheadAdjustedInterval.end))
      : await reportLegacy(
          marketLocation.id,
          device.id,
          toDate(dayAheadAdjustedInterval.start),
          toDate(dayAheadAdjustedInterval.end),
        );
  };

  const [revokeLegacy] = useRevokeLegacyDowntimeMutation({
    onError: (error) => showError(t(getErrorTranslationKey(error))),
    onCompleted: () => {
      showSuccess(
        t('DOWNTIME_MODAL_SNACKBAR_REVOKE_SUCCESS', {
          start: toDate(downtimeInterval.start),
          end: toDate(downtimeInterval.end),
        }),
      );
      onClose(null, 'successClick');
    },
  });
  const [revoke] = useRevokeDowntimeMutation({
    onError: (error) => showError(t(getErrorTranslationKey(error))),
    onCompleted: () => {
      showSuccess(
        t('DOWNTIME_MODAL_SNACKBAR_REVOKE_SUCCESS', {
          start: toDate(downtimeInterval.start),
          end: toDate(downtimeInterval.end),
        }),
      );
      onClose(null, 'successClick');
    },
  });

  const revokeDowntime = async (device: Device) => {
    const deadlineDate = getMarketingDeadline(getEarliestMarketingDeadline(device.deadlines));
    const dayAheadAdjustedInterval: Interval<Date> = {
      start: isAfter(downtimeInterval.start, deadlineDate)
        ? roundToNearestHours(toDate(downtimeInterval.start), { roundingMethod: 'floor' })
        : downtimeInterval.start,
      end: isAfter(downtimeInterval.end, deadlineDate)
        ? roundToNearestHours(toDate(downtimeInterval.end), { roundingMethod: 'ceil' })
        : downtimeInterval.end,
    };
    setDowntimeInterval(dayAheadAdjustedInterval);
    isVPP
      ? await revoke(device.internalId, toDate(dayAheadAdjustedInterval.start), toDate(dayAheadAdjustedInterval.end))
      : await revokeLegacy(device.id, toDate(dayAheadAdjustedInterval.start), toDate(dayAheadAdjustedInterval.end));
  };

  const onSelect = useCallback(
    ({ range }: { range: Interval }) => {
      const start = floorDate({ minutesStep })(toDate(range.start));
      const end = floorDate({ minutesStep })(toDate(range.end));
      setDowntimeInterval({
        start: isControlEnergyOnly ? controlEnergyIntervalStep(start) : start,
        end: isControlEnergyOnly ? controlEnergyIntervalStep(end, true) : end,
      });
    },
    [isControlEnergyOnly, minutesStep],
  );
  const sameDay = isSameDay(downtimeInterval.start, downtimeInterval.end);
  const interval =
    sameDay && downtimeInterval.start > downtimeInterval.end
      ? { start: downtimeInterval.start, end: addHours(downtimeInterval.start, isControlEnergyOnly ? 4 : 1) }
      : downtimeInterval;

  const optimizationStart = marketLocation.forecastProperties
    ?.find((prop) => prop.target === ForecastTarget.VPP)
    ?.validFrom.toLocaleString();

  const dateTimePickerProps: DateTimePickerProps = {
    label: t('SHARED_DATE_RANGE'),
    range: interval,
    hoursStep: isControlEnergyOnly ? 4 : 1,
    intradayOffset: intradayDeadline(device),
    onSelect,
    mode: 'range',
    timePickerProps: {
      from: {
        hoursOnly: !isFifteenMinutesResolution,
        minutesStep,
      },
      to: {
        hoursOnly: !isFifteenMinutesResolution,
        minutesStep,
      },
    },
    pickerProps: {
      disabled: {
        before: startOfDay(date),
        after:
          (optimizationStart && subSeconds(optimizationStart, 1)) ||
          (marketLocation.marketingEnd != null ? subSeconds(marketLocation.marketingEnd, 1) : undefined),
      },
      numberOfMonths: width > 720 ? 2 : 1,
      fixedWeeks: true,
      showWeekNumber: true,
      locale,
      startOfWeek,
      labels: {
        labelNext: () => t('DATE_TIME_PICKER_NEXT_MONTH'),
        labelPrevious: () => t('DATE_TIME_PICKER_PREVIOUS_MONTH'),
      },
    },
    validate: ({ range }: { range: Interval }) => {
      const earliestDayAheadDate = startOfHour(isControlEnergyOnly ? controlEnergyIntervalStep(date) : date);
      const earliestIntraDayDate = addMinutes(floorDate({ minutesStep })(date), intradayDeadline(device));
      if (isBefore(range.start, isVPP ? earliestIntraDayDate : earliestDayAheadDate)) {
        return [
          {
            start: {
              message: isVPP
                ? tv('DOWNTIME_START_TIME_MUST_NOT_BE_IN_THE_PAST_INTRADAY', { time: earliestIntraDayDate })
                : tv('DOWNTIME_START_TIME_MUST_NOT_BE_IN_THE_PAST'),
            },
          },
        ];
      }

      return [];
    },
  };

  const onCancel: MouseEventHandler<HTMLButtonElement> = (event) => {
    onClose(event, 'cancelClick');
    event?.stopPropagation();
  };

  const informationText = (() => {
    if (reportOrRevoke === 'report') {
      if (isWindOrSolar) return t('DOWNTIME_MODAL_INFORMATION_FLUCTUATING');
      else if (isControlEnergyOnly) return t('DOWNTIME_MODAL_INFORMATION_CONTROL_ENERGY');
      else return t('DOWNTIME_MODAL_INFORMATION');
    } else {
      return t('DOWNTIME_MODAL_INFORMATION_REVOKE');
    }
  })();

  return (
    <>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-col *:flex *:items-center *:gap-x-1 *:text-sm *:accent-green-200">
            <label>
              <input
                className="my-0"
                type="radio"
                name="data-selection"
                value="report"
                defaultChecked={reportOrRevoke === 'report'}
                onClick={() => setReportOrRevoke('report')}
              />
              <span>{t('DOWNTIME_MODAL_REPORT_MODE')}</span>
            </label>
            <label>
              <input
                className="my-0"
                type="radio"
                name="data-selection"
                value="revoke"
                defaultChecked={reportOrRevoke === 'revoke'}
                onClick={() => setReportOrRevoke('revoke')}
              />
              <span>{t('DOWNTIME_MODAL_REVOKE_MODE')}</span>
            </label>
          </div>
          {!isWindOrSolar && (
            <MasterDataItem
              className="!ml-0"
              header={t('FORECAST_ASSISTANT_TECHNICAL_UNIT')}
              body={getDisplayName(device)}
            />
          )}
          <DateTimePicker {...dateTimePickerProps} />
          <div className="text-xs leading-6">{informationText}</div>
        </div>
      </DialogContent>
      <footer>
        <DialogActions onClick={(e) => e.stopPropagation()}>
          <Button color="primary" disabled={loading} onClick={onCancel} variant="outlined">
            {t('SHARED_CANCEL')}
          </Button>
          <Button
            color="primary"
            disabled={loading || loadingLegacy}
            onClick={async (event) => {
              reportOrRevoke === 'report' ? await reportDowntime(device) : await revokeDowntime(device);
              event?.stopPropagation();
            }}
          >
            {reportOrRevoke === 'report' ? t('DOWNTIME_MODAL_SEND_BUTTON') : t('DOWNTIME_MODAL_REVOKE_BUTTON')}
          </Button>
        </DialogActions>
      </footer>
    </>
  );
};
